// Ba thẻ của người ĐIỀU HÀNH: "Toàn chuỗi", "Lịch tuần", "Mẫu & phụ cấp".
//
// Ba thẻ này nằm sau cổng quyền hẹp hơn hai thẻ kia, nên bài ở đây nói việc
// của người xếp lịch và người ban hành bảng kiểm — xem bảng phạm vi ở
// `../../branch-shift-guide-tabs.ts`.

import { BRANCH_SHIFT_TAB_LABEL } from "@/features/branch-shift/labels";
import type { GuideTab } from "../../types";

const DS: GuideTab[] = [
  {
    key: "chain",
    label: BRANCH_SHIFT_TAB_LABEL.chain,
    purpose: "Một dòng mỗi cơ sở: hôm nay ca nào đã mở, ca nào đã đóng, còn sự cố nào.",
    where: [
      "Mỗi dòng là một cơ sở, kèm tình trạng ca sáng và ca chiều của hôm nay.",
      "Cột cuối: số sự cố đang mở của cơ sở đó, kèm dấu cảnh báo khi còn tồn.",
      "Cơ sở chưa bật ca vận hành vẫn nằm trong danh sách, có ghi chú riêng.",
    ],
    howItWorks: [
      "Thẻ này chỉ nhìn NGÀY HÔM NAY, không phải cả tuần — muốn xem tuần thì sang thẻ lịch tuần.",
      "Danh sách đã tự giới hạn trong phạm vi cơ sở bạn phụ trách, nên hai người mở cùng lúc có thể thấy số dòng khác nhau.",
      "Cơ sở chưa bật ca vận hành vẫn được liệt kê chứ không ẩn đi: ẩn thì màn trông như trống rỗng và không ai biết vì sao.",
      "Số liệu ca chỉ đầy đủ sau khi ca được đóng, vì phần tổng kết tính lúc đóng ca.",
    ],
    statuses: [
      { label: "Chờ mở", meaning: "Đã có người trực nhưng chưa ai bấm mở ca." },
      { label: "Đang mở", meaning: "Ca đang chạy." },
      { label: "Đã đóng", meaning: "Ca xong và đã ghi báo cáo cuối ca." },
      { label: "Chưa xếp", meaning: "Hôm đó chưa ai được xếp trực ca này." },
      { label: "Chưa bật ca vận hành", meaning: "Cơ sở này chưa dùng tính năng ca." },
    ],
    blocks: [
      {
        for: ["lead", "exec", "qc"],
        title: "Nhìn nhanh các cơ sở",
        steps: [
          "Mở thẻ này đầu giờ để thấy cơ sở nào chưa mở ca.",
          "Cuối ngày mở lại để thấy cơ sở nào chưa đóng ca.",
          "Cơ sở còn sự cố đang mở thì sang thẻ sự cố xem chi tiết.",
          "Cơ sở hay đóng ca muộn hoặc hay bỏ trống mục bảng kiểm là chỗ nên đi kiểm.",
        ],
        troubles: [
          "Một cơ sở không hiện ca nào: cơ sở đó chưa bật ca vận hành, dòng của nó có ghi chú riêng.",
          "Số khách và doanh thu của một ca còn trống: ca chưa đóng, đừng đem con số đó đi so giữa các cơ sở.",
          "Thiếu cơ sở so với người khác nhìn: danh sách giới hạn theo phạm vi từng người, không phải mất dữ liệu.",
        ],
      },
    ],
  },
  {
    key: "week",
    label: BRANCH_SHIFT_TAB_LABEL.week,
    purpose: "Lưới bảy ngày của một cơ sở, để xếp ai trực ca nào.",
    where: [
      "Ô chọn cơ sở ở đầu thẻ; mỗi lần chỉ xếp cho một cơ sở.",
      "Lưới bảy cột là bảy ngày, mỗi ngày có ca sáng và ca chiều.",
      "Trong mỗi ô: chỗ chọn người trực ca đó, và nút xoá ca khi ca chưa mở.",
      "Nút lùi và tiến tuần ở đầu lưới.",
      "Ngay trên lưới có một dòng chữ nhỏ nhắc ca trưởng được làm gì trong giờ ca.",
    ],
    howItWorks: [
      "Danh sách người để chọn chỉ gồm nhân sự đang làm việc thuộc chính cơ sở đang xem.",
      "Người được xếp làm ca trưởng giữ nguyên công việc của mình, và trong giờ ca — dư 2 tiếng trước và sau — mở được Bảng suất cơ sở để đổi người trên các suất của cơ sở này. Hết giờ thì quyền đó tự hết, không phải thu lại.",
      "Ca đã mở thì không xoá được nữa — mở ca là ca đã chạy thật, xoá đi là mất bảng kiểm và ảnh của nó.",
      "Đổi người trực của một ca chưa mở thì làm ngay trong ô, không phải xoá rồi xếp lại.",
      "Xếp trước cho tuần sau là việc bình thường; lưới đi tới lui theo tuần.",
      "Ngày nào chưa xếp ai thì ca hôm đó bỏ trống, và bên thẻ toàn chuỗi sẽ thấy ngay.",
    ],
    statuses: [
      { label: "Chưa xếp", meaning: "Ca này chưa có người trực." },
      { label: "Chờ mở", meaning: "Đã xếp người nhưng chưa tới giờ hoặc chưa ai mở ca." },
      { label: "Đang mở", meaning: "Ca đang chạy — không xoá được nữa." },
      { label: "Đã đóng", meaning: "Ca đã xong." },
    ],
    blocks: [
      {
        for: ["lead", "exec"],
        title: "Xếp ca cho tuần",
        steps: [
          "Chọn cơ sở, rồi tiến tới tuần cần xếp.",
          "Với từng ngày, chọn người trực cho ca sáng và ca chiều.",
          "Xếp nhầm người thì đổi thẳng trong ô, không cần xoá ca.",
          "Xếp thừa một ca chưa mở thì xoá ô đó đi.",
          "Xếp xong rà lại cả lưới xem còn ngày nào bỏ trống không.",
          "Chọn ca trưởng là người thật sự có mặt cả ca: người đó còn đổi người trên Bảng suất cơ sở trong giờ ca.",
        ],
        troubles: [
          "Không xoá được một ca: ca đó đã mở rồi, xoá đi là mất bảng kiểm và ảnh — muốn đổi người thì đổi trong ô.",
          "Không tìm thấy tên người cần xếp: danh sách chỉ gồm nhân sự đang làm việc thuộc chính cơ sở này.",
          "Chọn người xong mà ô quay về như cũ (vẫn trống, hoặc vẫn là người trước) kèm dòng báo lỗi: lần xếp đó chưa được lưu — đọc lời báo rồi chọn lại.",
          "Xếp xong mà thẻ toàn chuỗi vẫn báo chưa xếp: xem lại bạn có đang đứng đúng tuần và đúng cơ sở không.",
        ],
      },
    ],
  },
  {
    key: "templates",
    label: BRANCH_SHIFT_TAB_LABEL.templates,
    purpose: "Nơi soạn danh mục việc trong bảng kiểm và treo ảnh chuẩn cho từng khu vực.",
    where: [
      "Danh sách mục việc của bảng kiểm mở ca và bảng kiểm đóng ca, sửa được từng dòng.",
      'Mỗi mục có ô "Ảnh mẫu:" và nút "+ Ảnh mẫu" để treo ảnh chuẩn của khu vực đó.',
      'Mỗi mục có ô chọn "Phần của": Quầy · Khu chụp · Makeup · Chung. Mục cũ chưa chọn hiện là "Chung".',
      'Nút "Phát hành version mới" ở cuối — chưa bấm thì mọi thay đổi mới chỉ là bản nháp.',
      "Đầu thẻ còn một ô đơn giá phụ cấp ca trưởng, kèm ghi chú nói khi nào ô này được dùng tới.",
    ],
    howItWorks: [
      "Bảng kiểm chạy theo BẢN: sửa xong phải phát hành thì bản mới mới có hiệu lực.",
      "Bản mới chỉ áp cho ca MỞ SAU lúc phát hành — ca đang chạy giữ nguyên danh mục lúc nó mở.",
      "Ảnh mẫu đi theo bản y như mục việc, nên ảnh mới cũng chỉ áp cho ca mở sau đó; nhờ vậy ảnh chụp của ca cũ còn đối chiếu được với ảnh mẫu đúng thời điểm ấy.",
      "Treo ảnh mẫu là cách nói rõ 'đạt' trông thế nào, thay vì để mỗi người hiểu một kiểu.",
      'Ô "Phần của" quyết ai chịu điểm khi mục đó hỏng. Từ 01/11/2026 mọi lễ tân trong ca (không làm ca trưởng) bị chấm bảng kiểm theo phần Quầy; phần Khu chụp, Makeup, Chung không tính cho lễ tân. Ca trưởng vẫn chấm cả ca như cũ.',
      'Mẫu chưa gắn mục nào là "Quầy" thì lễ tân chỉ bị chấm phần giờ mở ca và đóng ca — muốn chấm đủ thì gắn phần Quầy cho đúng các mục của quầy rồi phát hành.',
      "Ô đơn giá phụ cấp ở đây là đường lùi: sổ cơ chế lương đặt giá trị thì bên đó thắng.",
    ],
    statuses: [
      { label: "Bản nháp", meaning: "Đã sửa nhưng chưa phát hành, chưa ca nào dùng." },
      { label: "Đã phát hành", meaning: "Bản đang có hiệu lực cho các ca mở sau đó." },
    ],
    blocks: [
      {
        for: ["exec"],
        title: "Sửa bảng kiểm và treo ảnh mẫu",
        steps: [
          "Sửa, thêm hoặc bỏ mục việc trong bảng kiểm mở ca và đóng ca.",
          "Mục nào cần nhìn mới biết đạt hay chưa thì đánh dấu là mục cần ảnh.",
          'Chọn "Phần của" cho từng mục: việc của quầy lễ tân chọn "Quầy", việc ở phòng chụp chọn "Khu chụp", bàn trang điểm chọn "Makeup", việc chung cả cơ sở chọn "Chung".',
          'Bấm "+ Ảnh mẫu" ở mục đó và tải lên ảnh chuẩn của khu vực.',
          'Rà lại toàn bộ rồi bấm "Phát hành version mới".',
          "Báo cho các cơ sở biết có bản mới, vì ca đang chạy vẫn giữ danh mục cũ.",
        ],
        troubles: [
          "Phát hành bản mới mà ca đang chạy vẫn hiện danh mục cũ: đúng như vậy, bản mới chỉ áp cho ca MỞ SAU đó — chờ ca kế tiếp, đừng phát hành lại.",
          "Treo ảnh mẫu mới mà ca đang chạy vẫn hiện ảnh cũ: cùng lý do trên, ảnh mẫu đi theo bản.",
          "Sửa xong mà không cơ sở nào thấy: còn thiếu bước phát hành, thay đổi vẫn đang là bản nháp.",
          'Lễ tân hỏi vì sao bị trừ bảng kiểm dù mục hỏng là ở phòng chụp: kiểm lại ô "Phần của" của mục đó — gắn nhầm "Quầy" thì sửa rồi phát hành bản mới; ca đã chấm theo bản cũ giữ nguyên.',
        ],
      },
    ],
  },
];

/** Thẻ "Toàn chuỗi". Xuất theo TÊN để nơi gom bài không phải đếm vị trí. */
export const TAB_TOAN_CHUOI = DS[0]!;

/** Thẻ "Lịch tuần". */
export const TAB_LICH_TUAN = DS[1]!;

/** Thẻ "Mẫu & phụ cấp". */
export const TAB_MAU_PHU_CAP = DS[2]!;
